import { useCallback, useEffect, useRef, useState } from 'react';
import { useUpdateSkillMonthsExperience } from './useEmployeeSkillsMutations';

const DEBOUNCE_MS = 500;

type SaveMonthsExperience = (
  id: string,
  monthsExperience: number | null,
) => void;

export type UseDebouncedSerializedMonthsExperienceResult = {
  saveMonthsExperience: SaveMonthsExperience;
  isSaving: (id: string) => boolean;
};

/**
 * Saves of months-of-experience are debounced AND serialized per skill:
 * the user can keep editing while a previous save is in flight, and we
 * guarantee that the LAST value the user typed is the one that ends up
 * persisted — earlier in-flight responses can't overwrite a newer value
 * (Apollo's optimistic cache otherwise loses the race).
 *
 * `isSaving(id)` is true from the moment the user types until the queued
 * save chain settles, so the cell can render a spinner.
 */
export const useDebouncedSerializedMonthsExperience = (
  employeeId: string,
): UseDebouncedSerializedMonthsExperienceResult => {
  const { updateSkillMonthsExperience } = useUpdateSkillMonthsExperience();

  const pendingRef = useRef(new Map<string, number | null>());
  const inflightRef = useRef(new Set<string>());
  const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const [savingIds, setSavingIds] = useState<ReadonlySet<string>>(new Set());

  const recomputeSaving = useCallback(() => {
    setSavingIds(
      new Set<string>([
        ...inflightRef.current,
        ...timersRef.current.keys(),
        ...pendingRef.current.keys(),
      ]),
    );
  }, []);

  const flush = useCallback(
    async (id: string) => {
      if (inflightRef.current.has(id)) return;
      inflightRef.current.add(id);
      recomputeSaving();
      try {
        while (pendingRef.current.has(id)) {
          const monthsExperience = pendingRef.current.get(id) ?? null;
          pendingRef.current.delete(id);
          await updateSkillMonthsExperience({
            id,
            employeeId,
            monthsExperience,
          });
        }
      } finally {
        inflightRef.current.delete(id);
        recomputeSaving();
      }
    },
    [updateSkillMonthsExperience, employeeId, recomputeSaving],
  );

  // Cleanup uses a ref to the current flush() so the latest mutation function
  // is captured at unmount time without re-running this effect on every render.
  // We route unmount-time drains through the SAME per-id flush loop the runtime
  // path uses — firing in parallel would race any save still in flight for the
  // same skill id (responses could land out of order).
  const flushRef = useRef(flush);
  flushRef.current = flush;

  useEffect(() => {
    const timers = timersRef.current;
    const pending = pendingRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
      Array.from(pending.keys()).forEach((id) => {
        flushRef.current(id);
      });
    };
  }, []);

  const saveMonthsExperience = useCallback<SaveMonthsExperience>(
    (id, monthsExperience) => {
      pendingRef.current.set(id, monthsExperience);
      const existing = timersRef.current.get(id);
      if (existing) clearTimeout(existing);
      timersRef.current.set(
        id,
        setTimeout(() => {
          timersRef.current.delete(id);
          flush(id);
        }, DEBOUNCE_MS),
      );
      recomputeSaving();
    },
    [flush, recomputeSaving],
  );

  const isSaving = useCallback((id: string) => savingIds.has(id), [savingIds]);

  return { saveMonthsExperience, isSaving };
};
